import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DocumentsModal from './documentsModal/DocumentsModal';
import './Documentation.scss';
import { formatText, FormattedText } from 'core/i18n';
import { KEY_ENTER } from 'constants/keycode';
import { DocumentList } from './DocumentList';
import { DocumentationButton } from 'components/newAccountSteps/initialStep/documentation/DocumentationButton';
import { DocumentationHiddenButton } from 'components/newAccountSteps/initialStep/documentation/DocumentationHiddenButton';
import { useDocumentsModal } from 'components/newAccountSteps/initialStep/documentation/hooks/useDocumentsModal';
import { updateDocumentInsideStateList } from 'components/newAccountSteps/initialStep/documentation/updateDocumentInsideStateList';
import { findElementByTagAndText } from 'components/newAccountSteps/initialStep/documentation/findElementByTagAndText';
import { doTagClickMifid } from 'components/newAccountSteps/initialStep/documentation/tagging';

export const Documentation = ({
    profileOnboardingType,
    getDocuments,
    isDocumentsLoaded,
    precontractualDocNames,
    onDocumentAcceptance,
    onClose,
}) => {
    const [documentsList, setDocumentsList] = useState(precontractualDocNames);
    const documentationTextRef = useRef();
    const isAllDocumentsAccepted = useMemo(
        () => Array.isArray(documentsList) && documentsList.every(document => document.isAccepted),
        [documentsList]
    );
    const {
        showDocumentModal,
        documentsToOpen,
        setDocumentsToOpen,
        handleCloseDocumentsModal,
        handleShowDocumentsModal,
    } = useDocumentsModal({
        documentsList,
        isDocumentsLoaded,
        onLoadDocuments: callback => getDocuments(profileOnboardingType, callback),
        onClose,
    });

    const handleDocumentRead = documentReaded => {
        setDocumentsList(updateDocumentInsideStateList(documentReaded.documentType, { isRead: true }));
        setDocumentsToOpen(updateDocumentInsideStateList(documentReaded.documentType, { isRead: true }));
    };
    const handleDocumentAcceptance = documentAccepted => {
        setDocumentsList(updateDocumentInsideStateList(documentAccepted.documentType, { isAccepted: true }));
        onDocumentAcceptance(documentAccepted);
    };
    const handleShowDocumentsModalOnPressEnter = event => {
        event.persist();
        if (event.which !== KEY_ENTER) return;
        handleShowDocumentsModal();
    };

    useEffect(() => {
        setDocumentsList(precontractualDocNames);
    }, [precontractualDocNames]);

    useEffect(() => {
        const mifidElement = findElementByTagAndText({
            withIn: documentationTextRef.current,
            tag: 'a',
            text: 'mifid',
        });
        mifidElement?.addEventListener('click', doTagClickMifid);
        if (mifidElement) return () => mifidElement.removeEventListener('click', doTagClickMifid);
    }, [isAllDocumentsAccepted]);

    return (
        <>
            <div className="documentation-docs-container">
                <div>
                    <DocumentList documentsList={documentsList} onClickDocumentItem={handleShowDocumentsModal} />
                    <DocumentationHiddenButton
                        isAllDocumentsAccepted={isAllDocumentsAccepted}
                        onKeyDown={handleShowDocumentsModalOnPressEnter}
                    />
                </div>
                {!isAllDocumentsAccepted && (
                    <DocumentationButton documentsList={documentsList} onClick={() => handleShowDocumentsModal()} />
                )}
            </div>

            {isAllDocumentsAccepted && (
                <div>
                    <p ref={documentationTextRef} className="documentation-text">
                        <FormattedText value="field-acceptClausulePrecontractual" />
                    </p>
                </div>
            )}
            <DocumentsModal
                documentsList={documentsToOpen}
                showDocumentModal={showDocumentModal}
                handleClose={handleCloseDocumentsModal}
                onDocumentAcceptance={handleDocumentAcceptance}
                onDocumentRead={handleDocumentRead}
            />
        </>
    );
};

Documentation.propTypes = {
    isDocumentsLoaded: PropTypes.bool,
    profileOnboardingType: PropTypes.string,
    getDocuments: PropTypes.func,
    precontractualDocNames: PropTypes.array,
    onDocumentAcceptance: PropTypes.func,
};
